import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { format, addDays } from 'date-fns'
import { fr } from 'date-fns/locale'
import {
  CreditCard,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Loader,
  Euro,
  Calendar,
  TrendingUp,
  Building2,
  ArrowRight
} from 'lucide-react'
import { motion } from 'framer-motion'

import { useAuth } from '@/context/AuthContext'
import {
  getPendingReimbursements,
  checkOverdueReimbursements,
  calculateExpectedReimbursement,
  getEstimatedReimbursementDays,
  updateReimbursementStatus
} from '@/lib/reimbursement-tracker'
import type { MedicalInvoice, Reimbursement } from '@/types/health'

interface ReimbursementInvoice extends MedicalInvoice {
  reimbursement: Reimbursement & { processing_notes?: string | null }
  provider_name?: string | null
  insurance_provider?: string | null
  consultation_type?: string | null
}

interface ReimbursementItemProps {
  invoice: ReimbursementInvoice
  onStatusUpdate: () => void
}

function ReimbursementStatusBadge({ status }: { status: string }) {
  const statusConfig = {
    pending: { icon: Clock, label: 'En attente', className: 'bg-yellow-900/40 text-yellow-300 border-yellow-800/40' },
    processing: { icon: Loader, label: 'En traitement', className: 'bg-blue-900/40 text-blue-300 border-blue-800/40' },
    approved: { icon: CheckCircle, label: 'Approuvé', className: 'bg-green-900/40 text-green-300 border-green-800/40' },
    rejected: { icon: XCircle, label: 'Rejeté', className: 'bg-red-900/40 text-red-300 border-red-800/40' },
    paid: { icon: CheckCircle, label: 'Remboursé', className: 'bg-emerald-900/40 text-emerald-300 border-emerald-800/40' }
  }

  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending
  const Icon = config.icon

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${config.className}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  )
}

function ReimbursementItem({ invoice, onStatusUpdate }: ReimbursementItemProps) {
  const [updating, setUpdating] = useState(false)
  const providerName = invoice.provider_name || invoice.practitioner_name || invoice.medical_facility || 'Facture médicale'

  const expectedAmount = calculateExpectedReimbursement(
    invoice.total_amount,
    invoice.insurance_provider,
    invoice.consultation_type
  )

  const estimatedDays = getEstimatedReimbursementDays(invoice.insurance_provider)
  const submissionDate = new Date(invoice.reimbursement.created_at)
  const estimatedDate = addDays(submissionDate, estimatedDays)

  const handleStatusUpdate = async (newStatus: string, amount?: number) => {
    setUpdating(true)
    try {
      await updateReimbursementStatus(invoice.id, newStatus as any, amount)
      onStatusUpdate()
    } catch (error) {
      console.error('Erreur mise à jour:', error)
    } finally {
      setUpdating(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-dark-surface border border-dark-border rounded-xl p-4"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h4 className="font-semibold text-white">{providerName}</h4>
              <p className="text-sm text-slate-400">
                {format(new Date(invoice.service_date), 'd MMM yyyy', { locale: fr })}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-slate-400 mb-1">Montant facturé</p>
              <p className="font-semibold text-white">{invoice.total_amount.toFixed(2)}€</p>
            </div>
            <div>
              <p className="text-slate-400 mb-1">Remboursement attendu</p>
              <p className="font-semibold text-green-400">{expectedAmount.toFixed(2)}€</p>
            </div>
            <div>
              <p className="text-slate-400 mb-1">Date estimée</p>
              <p className="font-semibold text-white">
                {format(estimatedDate, 'd MMM', { locale: fr })}
              </p>
            </div>
            <div>
              <p className="text-slate-400 mb-1">Assurance</p>
              <p className="font-semibold text-white text-xs">
                {invoice.insurance_provider || 'Non renseignée'}
              </p>
            </div>
          </div>
        </div>

        <div className="text-right">
          <ReimbursementStatusBadge status={invoice.reimbursement.status} />

          {invoice.reimbursement.status === 'pending' && (
            <div className="mt-3 space-x-2">
              <button
                onClick={() => handleStatusUpdate('processing')}
                disabled={updating}
                className="btn-secondary text-xs"
              >
                Marquer en traitement
              </button>
            </div>
          )}

          {invoice.reimbursement.status === 'processing' && (
            <div className="mt-3 space-x-2">
              <button
                onClick={() => handleStatusUpdate('paid', expectedAmount)}
                disabled={updating}
                className="btn-primary text-xs"
              >
                Marquer payé
              </button>
            </div>
          )}
        </div>
      </div>

      {invoice.reimbursement.processing_notes && (
        <div className="mt-4 p-3 bg-dark-surface-high border border-dark-border rounded-lg">
          <p className="text-sm text-slate-300">{invoice.reimbursement.processing_notes}</p>
        </div>
      )}
    </motion.div>
  )
}

export default function ReimbursementTracker() {
  const { user } = useAuth()

  const { data: pendingReimbursements, isLoading, refetch } = useQuery({
    queryKey: ['pending-reimbursements', user?.id],
    queryFn: () => getPendingReimbursements(user!.id),
    enabled: Boolean(user)
  })

  const { data: overdueReimbursements } = useQuery({
    queryKey: ['overdue-reimbursements', user?.id],
    queryFn: () => checkOverdueReimbursements(user!.id),
    enabled: Boolean(user)
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader className="w-6 h-6 animate-spin text-blue-400" />
        <span className="ml-3 text-slate-400">Chargement des remboursements...</span>
      </div>
    )
  }

  const totalPendingAmount = pendingReimbursements?.reduce((sum, item) => {
    return sum + calculateExpectedReimbursement(
      item.total_amount,
      item.insurance_provider,
      item.consultation_type
    )
  }, 0) || 0

  return (
    <div className="space-y-6">
      {/* Statistiques rapides */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-dark-surface border border-dark-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8 text-yellow-400" />
            <div>
              <p className="text-2xl font-bold text-white">
                {pendingReimbursements?.length || 0}
              </p>
              <p className="text-sm text-slate-400">En attente</p>
            </div>
          </div>
        </div>

        <div className="bg-dark-surface border border-dark-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Euro className="w-8 h-8 text-green-400" />
            <div>
              <p className="text-2xl font-bold text-white">
                {totalPendingAmount.toFixed(0)}€
              </p>
              <p className="text-sm text-slate-400">Montant attendu</p>
            </div>
          </div>
        </div>

        <div className="bg-dark-surface border border-dark-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-red-400" />
            <div>
              <p className="text-2xl font-bold text-white">
                {overdueReimbursements?.length || 0}
              </p>
              <p className="text-sm text-slate-400">En retard</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alertes remboursements en retard */}
      {overdueReimbursements && overdueReimbursements.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-900/20 border border-red-800/40 rounded-xl p-4"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-300 mb-2">Remboursements en retard</h3>
              <p className="text-sm text-red-200/80 mb-3">
                {overdueReimbursements.length} remboursement(s) dépassent les délais habituels.
              </p>
              <div className="space-y-2">
                {overdueReimbursements.map(({ invoice, daysOverdue }) => (
                  <div key={invoice.id} className="text-xs text-red-200/70">
                    <span className="font-medium">
                      {(invoice as ReimbursementInvoice).provider_name || invoice.practitioner_name || invoice.medical_facility || 'Facture médicale'}
                    </span> -
                    En retard de {daysOverdue} jour(s)
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Liste des remboursements en cours */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Remboursements en cours</h3>
          <button
            onClick={() => refetch()}
            className="btn-secondary text-sm"
          >
            Actualiser
          </button>
        </div>

        {pendingReimbursements && pendingReimbursements.length > 0 ? (
          <div className="space-y-4">
            {pendingReimbursements.map((item) => (
              <ReimbursementItem
                key={item.id}
                invoice={item}
                onStatusUpdate={refetch}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Tous les remboursements sont à jour !</h3>
            <p className="text-slate-400">
              Aucun remboursement en attente. Vos factures sont traitées.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
